import React, { useMemo } from "react";
import { Spinner } from "@kui-react/spinner";
import { FlatCard } from "components/UI-lib";
import badnotebook from "components/Notebooks/badnotebook.png";
import type { NotebookCell } from "server/corsproxy/remoteContent/api";
import { useRemoteNotebookQuery } from "server/corsproxy/remoteContent/queries";
import { SafeMarkdown } from "./SafeMarkdown";
import {
  getNotebookLanguage,
  highlightNotebookCode,
  joinNotebookSource,
} from "./notebookContent";
import { NotebookOutputBlock } from "./NotebookOutput";

interface NotebookViewerProps {
  file: string;
}

const CodeCell: React.FC<{ cell: NotebookCell; language: string }> = ({
  cell,
  language,
}) => {
  const code = joinNotebookSource(cell.source);
  const highlighted = useMemo(
    () => highlightNotebookCode(code, language),
    [code, language],
  );
  const outputs = cell.outputs || [];

  return (
    <div className="my-4 rounded-lg overflow-hidden border border-zinc-800">
      <pre className="bg-black p-4 overflow-x-auto text-sm m-0">
        <code
          className={`language-${language}`}
          dangerouslySetInnerHTML={{ __html: highlighted }}
        />
      </pre>
      {outputs.length > 0 && (
        <div className="bg-zinc-900 border-t border-zinc-800">
          {outputs.map((output, idx) => (
            <NotebookOutputBlock key={idx} output={output} />
          ))}
        </div>
      )}
    </div>
  );
};

const MarkdownCell: React.FC<{ cell: NotebookCell }> = ({ cell }) => (
  <div className="my-2 max-w-none">
    <SafeMarkdown>{joinNotebookSource(cell.source)}</SafeMarkdown>
  </div>
);

const NotebookViewer: React.FC<NotebookViewerProps> = ({ file }) => {
  const {
    data: notebook,
    isInitialLoading,
    isError,
  } = useRemoteNotebookQuery(file);

  const language = useMemo(
    () => (notebook ? getNotebookLanguage(notebook) : "python"),
    [notebook],
  );

  if (isInitialLoading) {
    return (
      <div className="flex flex-col justify-center items-center py-8">
        <Spinner className="w-24 h-24" />
        <p className="text-sm text-secondary mt-2">Loading Notebook...</p>
      </div>
    );
  }

  if (isError || !notebook) {
    return (
      <div className="flex flex-col justify-center items-center mt-6">
        <img src={badnotebook} alt="Notebook not found" />
      </div>
    );
  }

  return (
    <FlatCard>
      <div className="max-w-none">
        {(notebook.cells || []).map((cell, idx) => {
          if (cell.cell_type === "markdown") {
            return <MarkdownCell key={idx} cell={cell} />;
          }
          if (cell.cell_type === "code") {
            return <CodeCell key={idx} cell={cell} language={language} />;
          }
          return null;
        })}
      </div>
    </FlatCard>
  );
};

export default NotebookViewer;
